import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
} from '@angular/core';
import { NgCommonModule } from '@principle-theorem/ng-shared';
import { isCustomWeeklyPattern } from '@principle-theorem/principle-core';
import {
  CustomWeeklyPattern,
  DAY_MONTH_YEAR_FORMAT,
  IFrequencyDisplay,
  IRecurrencePattern,
  RECURRENCE_FREQUENCY_DISPLAY_MAP,
  RequireProps,
  Time24hrType,
  titlecase,
} from '@principle-theorem/shared';

@Component({
  selector: 'pr-recurrence-summary',
  templateUrl: './recurrence-summary.component.html',
  styleUrls: ['./recurrence-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'inline' },
  imports: [NgCommonModule],
})
export class RecurrenceSummaryComponent {
  readonly dateFormat = DAY_MONTH_YEAR_FORMAT;

  recurrence = input.required<IRecurrencePattern>();
  runTime = input<Time24hrType | undefined>(undefined);
  frequencyLabel = computed(() => this._frequencyLabel(this.recurrence()));

  private _frequencyLabel(frequency?: IRecurrencePattern): string {
    if (!frequency?.customFrequencyType) {
      return 'unknown';
    }

    const unit = RECURRENCE_FREQUENCY_DISPLAY_MAP.find(
      (display) => display.value === frequency.customFrequencyType
    );

    const label = this._getDefaultFrequencyLabel(
      frequency as RequireProps<IRecurrencePattern, 'customFrequencyType'>,
      unit
    );

    if (isCustomWeeklyPattern(frequency)) {
      return this._getWeeklyFrequencyLabel(frequency, unit);
    }

    return label;
  }

  private _getDefaultFrequencyLabel(
    frequency: RequireProps<IRecurrencePattern, 'customFrequencyType'>,
    unit?: IFrequencyDisplay
  ): string {
    if (frequency.seperationCount === 1) {
      return unit ? unit.singularDisplayName : frequency.customFrequencyType;
    }

    return unit
      ? `${frequency.seperationCount} ${unit.displayName}`
      : frequency.customFrequencyType;
  }

  private _getWeeklyFrequencyLabel(
    frequency: IRecurrencePattern & CustomWeeklyPattern,
    unit?: IFrequencyDisplay
  ): string {
    const label = this._getDefaultFrequencyLabel(frequency, unit);
    if (!frequency.daysOfWeek?.length) {
      return label;
    }

    const days = frequency.daysOfWeek
      .map((DayOfWeek) => titlecase(DayOfWeek))
      .join(' / ');

    return `${label} on ${days}`;
  }
}
